import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { authApi } from '../api';
import { setSessionExpiredHandler, tokenStore } from '../api/client';

/**
 * Session state for the console.
 *
 * Access is Google SSO restricted to a whitelisted domain, enforced by the
 * backend at `/auth/google`. Every backoffice admin holds the same authority —
 * the tier that used to sit above them was retired — so a session is either
 * usable or absent, with no third "authenticated but not permitted" state.
 *
 * The backoffice API has no `/auth/me`, so a stored token is verified on boot
 * by probing an endpoint that requires one. Doing it once here means screens
 * can assume a usable session rather than each discovering a 403 alone.
 */

type SessionState = 'checking' | 'anonymous' | 'authenticated';

interface AuthValue {
  state: SessionState;
  email: string | null;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

const EMAIL_KEY = 'seiki_backoffice_email';

/**
 * Decode a Google Identity Services ID token (JWT) without verifying the
 * signature — verification happens on the backend. We only use this
 * client-side to extract the admin's email for display / localStorage.
 */
function decodeIdToken(idToken: string): { email?: string; name?: string } {
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    return { email: payload.email, name: payload.name };
  } catch {
    return {};
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>('checking');
  const [email, setEmail] = useState<string | null>(
    () => window.localStorage.getItem(EMAIL_KEY),
  );

  const clearSession = useCallback(() => {
    tokenStore.clear();
    window.localStorage.removeItem(EMAIL_KEY);
    setEmail(null);
    setState('anonymous');
  }, []);

  // The axios layer discovers an unusable token before any screen does; let it
  // drive us back to the login view rather than duplicating that detection.
  useEffect(() => {
    setSessionExpiredHandler(() => clearSession());
  }, [clearSession]);

  useEffect(() => {
    let cancelled = false;

    async function verifyStoredToken() {
      if (!tokenStore.getAccess()) {
        setState('anonymous');
        return;
      }
      try {
        await authApi.probeSession();
        if (!cancelled) setState('authenticated');
      } catch {
        // Nothing to distinguish any more: a token that cannot call the API is
        // a token to discard.
        if (!cancelled) clearSession();
      }
    }

    verifyStoredToken();
    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  // Dagster-style Google SSO: the browser obtains a Google ID token (JWT) via
  // Google Identity Services, hands it to us, and we send it to the backend's
  // /auth/google endpoint. The backend verifies it, checks the domain
  // whitelist, and returns the same backoffice JWT pair the password flow uses.
  const loginWithGoogle = useCallback(async (idToken: string) => {
    const googleEmail = decodeIdToken(idToken).email ?? null;
    const payload = await authApi.googleLogin(idToken);
    tokenStore.set(payload.access_token, payload.refresh_token);
    if (googleEmail) {
      window.localStorage.setItem(EMAIL_KEY, googleEmail);
      setEmail(googleEmail);
    }

    // The token is fresh, so this only confirms the admin record is usable —
    // a failure here is a backend problem worth surfacing, not a role denial.
    try {
      await authApi.probeSession();
      setState('authenticated');
    } catch (err) {
      clearSession();
      throw err;
    }
  }, [clearSession]);

  const logout = useCallback(async () => {
    const refresh = tokenStore.getRefresh();
    if (refresh) {
      // Best-effort revocation: a failed logout call must still clear the
      // client session, or the user is stuck signed in to a dead token.
      try {
        await authApi.logout(refresh);
      } catch {
        /* ignore */
      }
    }
    clearSession();
  }, [clearSession]);

  const value = useMemo<AuthValue>(
    () => ({ state, email, loginWithGoogle, logout }),
    [state, email, loginWithGoogle, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
